































import { defineComponent } from "@vue/composition-api";
import { isAfter, isBefore, isValid, isEqual, parseISO, format, differenceInCalendarDays, addDays } from "date-fns";
import { $t } from "@/lib/helpers/i18nHelper";
import { DateRangePickerValue } from "@/types";

export default defineComponent({
  name: "DateRangePicker",
  props: {
    label: { type: String, default: "" },
    placeholder: { type: String, default: $t("Select date") },
    inputProps: { type: Object, default: () => ({}) },
    single: { type: Boolean, default: false },
    allowedDates: { type: Function, default: () => true },
    closeOnSelect: { type: Boolean, default: false },
    value: { type: Object as () => DateRangePickerValue, default: () => ({}) },
    highlightColor: { type: String, default: "blue lighten-5" },
  },
  data() {
    return {
      menu: false,
      currentSelection: "" as string | "",
      dateStart: "",
      dateEnd: "",
    };
  },
  computed: {
    dateStartObj(): Date {
      return parseISO(this.dateStart);
    },
    dateEndObj(): Date {
      return parseISO(this.dateEnd);
    },
    highlightDates(): string[] {
      const start = this.dateStartObj;
      const end = this.dateEndObj;
      if (!isValid(start) || !isValid(end)) return [];
      const selectedDays = Math.abs(differenceInCalendarDays(start, end)) + 1;

      return Array.from(new Array(selectedDays)).map((item, index) => {
        item = this.formatDate(addDays(start, index));
        return item;
      });
    },
    highlightClasses(): { [currentDate: string]: string } {
      const start = this.dateStartObj;
      const end = this.dateEndObj;

      return this.highlightDates.reduce((out, currentDate) => {
        const dateObj = parseISO(currentDate);
        const classes = [this.highlightColor, "date-range-picker__in-range"];
        if (isEqual(dateObj, start)) classes.push("_start-date");
        if (isEqual(dateObj, end)) classes.push("_end-date");
        out[currentDate] = classes.join(" ");
        return out;
      }, {} as { [currentDate: string]: string });
    },
    textValue(): string {
      const formatCode = "MM/dd/yyyy";
      const start = this.dateStartObj;
      const end = this.dateEndObj;
      if (!isValid(start) || !isValid(end)) return "";
      if (isEqual(start, end)) {
        return this.formatDate(start, formatCode);
      } else {
        return `${this.formatDate(start, formatCode)} - ${this.formatDate(end, formatCode)}`;
      }
    },
  },
  watch: {
    value: {
      handler({ dateStart, dateEnd }: DateRangePickerValue) {
        this.dateStart = dateStart;
        this.dateEnd = dateEnd;
      },
      immediate: true,
    },
  },
  methods: {
    formatDate(date: Date, formatCode = "yyyy-MM-dd") {
      return format(date, formatCode);
    },
    handleSelection(input: string) {
      const curSelection = parseISO(input);
      const prevSelection = parseISO(this.currentSelection);
      this.currentSelection = this.formatDate(curSelection);
      if (!isValid(prevSelection) || this.single) {
        this.dateStart = this.formatDate(curSelection);
        this.dateEnd = this.formatDate(curSelection);
      } else if (isEqual(curSelection, prevSelection)) {
        this.dateStart = this.formatDate(curSelection);
        this.dateEnd = this.formatDate(curSelection);
      } else if (isBefore(curSelection, prevSelection)) {
        this.dateStart = this.formatDate(curSelection);
        this.dateEnd = this.formatDate(prevSelection);
      } else if (isAfter(curSelection, prevSelection)) {
        this.dateStart = this.formatDate(prevSelection);
        this.dateEnd = this.formatDate(curSelection);
      }
      this.sendEvent();
      if (this.closeOnSelect) this.menu = false;
    },
    clearDates() {
      this.currentSelection = "";
      this.dateEnd = "";
      this.dateStart = "";
      this.sendEvent();
    },
    sendEvent() {
      if ((isValid(this.dateStartObj) && isValid(this.dateEndObj)) || (this.dateStart === "" && this.dateEnd === "")) {
        const data: DateRangePickerValue = {
          dateStart: this.dateStart,
          dateEnd: this.dateEnd,
        };
        this.$emit("input", data);
      }
    },
  },
});
